import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useIsMobile } from "../hooks/useIsMobile";

const TABS = [
  { id: "map",   label: "Missions", icon: <MissionsIcon /> },
  { id: "stats", label: "Stats",    icon: <StatsIcon /> },
  { id: "words", label: "Lexicon",  icon: <LexiconIcon /> },
];

export default function Header({ tab, onTab, streak, onReset, level = 1 }) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { logout(); toast.info("Logged out successfully"); };
  const handleReset = () => {
    if (window.confirm("Reset all progress? This cannot be undone.")) {
      onReset(); toast.success("Progress reset");
    }
  };

  return (
    <>
      <header style={{ background: "#fff", borderBottom: "1px solid #ede8df", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ width: "100%", padding: isMobile ? "0 16px" : "0 28px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#1a8f68", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TulipIcon size={19} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#111", letterSpacing: -0.5 }}>Parsian</div>
              {!isMobile && <div style={{ fontSize: 9, color: "#b0a898", letterSpacing: 2, textTransform: "uppercase", marginTop: -1 }}>Learn Persian</div>}
            </div>
          </div>

          {/* Desktop Nav */}
          {!isMobile && (
            <div style={{ display: "flex", gap: 2 }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => onTab(t.id)} style={{
                  padding: "7px 16px", fontSize: 13, fontWeight: 600,
                  color: tab === t.id ? "#1a8f68" : "#a09890",
                  borderRadius: 10, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 7,
                  border: "none", background: tab === t.id ? "#e8f7f1" : "none",
                  fontFamily: "inherit", transition: "all 0.15s",
                }}>
                  <span style={{ width: 15, height: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Right */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8 }}>
            {/* Streak */}
            {streak > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, padding: isMobile ? "5px 8px" : "6px 12px", borderRadius: 9, background: "#fffbf0", border: "1.5px solid #f0c040", fontSize: 12, fontWeight: 700, color: "#8a6200" }}>
                <FlameIcon />
                {streak}
              </div>
            )}

            {/* XP */}
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, padding: isMobile ? "5px 8px" : "6px 12px", borderRadius: 9, background: "#f0fbf6", border: "1.5px solid #4ec99a", fontSize: 12, fontWeight: 700, color: "#0a6644" }}>
                <StarIcon />
                {user.xp || 0} XP
              </div>
            )}

            {!isMobile && <div style={{ width: 1, height: 24, background: "#ede8df", margin: "0 2px" }} />}

            {/* User menu */}
            {user && (
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: isMobile ? 0 : 8,
                    padding: isMobile ? "2px" : "4px 8px 4px 4px", borderRadius: 22,
                    border: "1.5px solid #ede8df", background: "#fff",
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  <div style={{ padding: 2, borderRadius: "50%", background: "linear-gradient(135deg,#1a8f68,#4ec99a)" }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#1a8f68", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 800, border: "2px solid #fff" }}>
                      {user.name?.[0]?.toUpperCase() || "Y"}
                    </div>
                  </div>
                  {!isMobile && (
                    <>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 10, color: "#b0a898", lineHeight: 1 }}>Hey there,</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#111", lineHeight: 1.3 }}>{user.name?.split(" ")[0]}</div>
                      </div>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#b0a898" strokeWidth="1.5" strokeLinecap="round">
                        <path d={menuOpen ? "M2 7l3-3 3 3" : "M2 3l3 3 3-3"}/>
                      </svg>
                    </>
                  )}
                </button>

                {menuOpen && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 200,
                    background: "#fff", border: "1px solid #ede8df", borderRadius: 14,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)", overflow: "hidden", zIndex: 200,
                  }}>
                    <div style={{ padding: "14px 16px", borderBottom: "1px solid #f5f0e8" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>{user.name}</div>
                      <div style={{ fontSize: 11, color: "#b0a898" }}>{user.email}</div>
                      <div style={{ fontSize: 11, color: "#1a8f68", fontWeight: 600, marginTop: 3 }}>Level {level} · {user.xp || 0} XP</div>
                    </div>
                    <button onClick={() => { handleReset(); setMenuOpen(false); }} style={menuItemStyle}>
                      <ResetIcon /> Reset progress
                    </button>
                    <button onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ ...menuItemStyle, color: "#e24b4a" }}>
                      <LogoutIcon /> Log out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile bottom tab bar */}
      {isMobile && (
        <nav style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
          background: "#fff", borderTop: "1px solid #ede8df",
          display: "flex", alignItems: "center",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => onTab(t.id)} style={{
              flex: 1, padding: "10px 0 8px", border: "none", background: "none",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              cursor: "pointer", fontFamily: "inherit",
              color: tab === t.id ? "#1a8f68" : "#b0a898",
            }}>
              <span style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
                background: tab === t.id ? "#e8f7f1" : "none", borderRadius: 8, padding: 3,
              }}>{t.icon}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>{t.label}</span>
            </button>
          ))}
        </nav>
      )}
    </>
  );
}

const menuItemStyle = {
  display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "11px 16px",
  background: "none", border: "none", textAlign: "left", fontSize: 13, fontWeight: 500,
  color: "#555", cursor: "pointer", fontFamily: "inherit",
};

function TulipIcon({ size = 19 }) {
  return <svg width={size} height={size} viewBox="0 0 22 26" fill="none">
    <line x1="11" y1="25" x2="11" y2="16" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M11 20 Q7 18 6.5 14 Q9 15.5 11 19" fill="white" opacity="0.7"/>
    <path d="M11 16 Q6 12 6 7.5 Q8.5 5.5 11 9 Q13.5 5.5 16 7.5 Q16 12 11 16Z" fill="white"/>
    <path d="M11 15 Q9 12 9.5 8.5 Q10.5 11 11 14 Q11.5 11 12.5 8.5 Q13 12 11 15Z" fill="#1a8f68" opacity="0.4"/>
  </svg>;
}

function MissionsIcon() { return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 6.5L8 1.5l6.5 5V15H10v-4H6v4H1.5V6.5z"/></svg>; }
function StatsIcon() { return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="8" width="3.5" height="7" rx="1"/><rect x="6" y="4.5" width="3.5" height="10.5" rx="1"/><rect x="11" y="1.5" width="3.5" height="13.5" rx="1"/></svg>; }
function LexiconIcon() { return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 2.5h8a1 1 0 011 1v9a1 1 0 01-1 1H2V2.5z"/><path d="M10.5 5.5H13a1 1 0 011 1v5a1 1 0 01-1 1h-2.5"/><line x1="4.5" y1="5.5" x2="8" y2="5.5"/><line x1="4.5" y1="8.5" x2="8" y2="8.5"/></svg>; }
function FlameIcon() { return <svg width="13" height="13" viewBox="0 0 14 14" fill="#e8900a"><path d="M7.5 1C7.5 1 4.5 4.5 4.5 7.5C4.5 9.4 5.8 11 7.5 11C9.2 11 10.5 9.4 10.5 7.5C10.5 5.8 9.2 3.8 9.2 3.8C9.2 3.8 8.8 5.5 7.5 5.5C6.6 5.5 6 4.5 6 4.5C6 4.5 7.5 3 7.5 1Z"/></svg>; }
function StarIcon() { return <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#1a8f68" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="7,1 8.8,5.2 13.5,5.5 9.8,8.8 11,13.5 7,11 3,13.5 4.2,8.8 0.5,5.5 5.2,5.2"/></svg>; }
function ResetIcon() { return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 7a5 5 0 109.9-1"/><path d="M12 2v4h-4"/></svg>; }
function LogoutIcon() { return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M9 2h3v10H9M6 10l3-3-3-3M9 7H2"/></svg>; }
